/**
 * Accès au domaine GRV, par hooks TanStack Query.
 *
 * Les clés de cache portent les filtres : changer un filtre change la clé, donc la requête, sans
 * gestion manuelle d'invalidation. Une mise à jour de fiche invalide les listes **et** la fiche
 * concernée, faute de quoi l'écran afficherait la valeur d'avant.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, requete, type Reponse } from "../../shared/api/client";
import type {
  AssociationTracker,
  ChangementStatut,
  FicheGrv,
  MiseAJourGrv,
  ValeurReferentielActive,
} from "../../shared/api/types";

export type FiltresParc = {
  client?: string;
  statut?: string;
  pageSize?: number;
  cursor?: string | null;
};

export const clesGrv = {
  parc: (filtres: FiltresParc) => ["grv", "parc", filtres] as const,
  fiche: (numero: string) => ["grv", "fiche", numero] as const,
};

export function useParc(filtres: FiltresParc) {
  return useQuery({
    queryKey: clesGrv.parc(filtres),
    queryFn: () =>
      api.lire<FicheGrv[]>(
        "/v1/grv" +
          requete({
            "filter[client]": filtres.client,
            "filter[statut]": filtres.statut,
            pageSize: filtres.pageSize,
            cursor: filtres.cursor,
          }),
      ),
    // Les positions sont rafraîchies par le polling Positrex toutes les 15 à 30 minutes : rejouer la
    // requête plus souvent que ça ne montrerait rien de nouveau et facturerait des lectures.
    staleTime: 60_000,
  });
}

export function useFicheGrv(numero: string | undefined) {
  return useQuery({
    queryKey: clesGrv.fiche(numero ?? ""),
    queryFn: () => api.lire<FicheGrv>(`/v1/grv/${encodeURIComponent(numero!)}`),
    enabled: Boolean(numero),
  });
}

/**
 * Statuts opérationnels et produits transportés actifs, pour les menus déroulants du formulaire de
 * modification de la fiche GRV (`OngletInformations.tsx`).
 *
 * `GET /v1/grv/...` plutôt que `GET /v1/admin/referentiels/{type}` : ce dernier est réservé à
 * l'administrateur, alors qu'un opérationnel a le droit de modifier ces deux champs.
 */
export function useStatutsOperationnels() {
  return useQuery({
    queryKey: ["grv", "statuts-operationnels"],
    queryFn: () => api.lire<ValeurReferentielActive[]>("/v1/grv/statuts-operationnels"),
    staleTime: 5 * 60_000,
  });
}

export function useProduitsTransportes() {
  return useQuery({
    queryKey: ["grv", "produits-transportes"],
    queryFn: () => api.lire<ValeurReferentielActive[]>("/v1/grv/produits-transportes"),
    staleTime: 5 * 60_000,
  });
}

export function useMettreAJourGrv(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: MiseAJourGrv): Promise<Reponse<FicheGrv>> =>
      api.modifier<FicheGrv>(`/v1/grv/${encodeURIComponent(numero)}`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["grv"] });
    },
  });
}

/**
 * Association, remplacement ou désassociation du tracker (`PUT /v1/grv/{numero}/tracker`).
 *
 * Distincte de `useMettreAJourGrv` : l'opération historise systématiquement l'événement dans la
 * fiche de vie (`docs/DECISIONS.md` D57), d'où l'invalidation de cette requête en plus de la fiche.
 */
export function useAssocierTracker(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: AssociationTracker): Promise<Reponse<FicheGrv>> =>
      api.remplacer<FicheGrv>(`/v1/grv/${encodeURIComponent(numero)}/tracker`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["grv"] });
      void client.invalidateQueries({ queryKey: ["maintenance", "fiche-de-vie", numero] });
    },
  });
}

/**
 * Changement de statut opérationnel (`PATCH /v1/grv/{numero}/statut`, `docs/DECISIONS.md` D87).
 *
 * Distincte de `useMettreAJourGrv`, sur le même principe que `useAssocierTracker` : historise
 * systématiquement l'événement dans la fiche de vie.
 */
export function useChangerStatut(numero: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: ChangementStatut): Promise<Reponse<FicheGrv>> =>
      api.modifier<FicheGrv>(`/v1/grv/${encodeURIComponent(numero)}/statut`, corps),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["grv"] });
      void client.invalidateQueries({ queryKey: ["maintenance", "fiche-de-vie", numero] });
    },
  });
}
